// Layer 2-adjacent (Engine-fed data assembly): the CRO Pb-recovery thread's
// answer (#273, paul-fmea.A6) — direct retrieval only, no free prose. Every
// line below is joined from the live rule set + the Met Diagnostics register,
// computed at render time from the session's own `AdvisorySessionState`
// (never frozen at module load), so the thread can never present a stale
// `rs-vN` as current — review MUST-FIX M4. See
// docs/logic/workspace/chat-rail.md § Rule-set retrieval thread.

import type { DiagnosticRule } from '@/versions/v1/schema/diagnostic-rule';
import type { ChatCitation } from './chat-types';
import type { CitationSource } from './citation-registry';
import type { AdvisorySessionState } from './AdvisoryEngine';
import { allRules } from './diagnostic-rule.transform';
import { MET_DIAGNOSTICS_CAUSES } from './bunker-hill-met-diagnostics-register';
import { liveRuleSet } from './RuleSetEngine';

export const PB_RECOVERY_SECTION = 'Pb Recovery Below Target' as const;

export type PbRecoveryCauseCheck = {
  causeId: string;
  cause: string;
  /** Exactly one of the three states the Advisory rule-set panel uses, same
   * words (review MUST-FIX M2/M8 ruling): `covered` (a live rule names it),
   * `proposed` (only a not-yet-approved rule does), `gap` (neither). */
  state: 'covered' | 'proposed' | 'gap';
  rules: readonly DiagnosticRule[];
};

export type PbRecoveryRetrieval = {
  /** The live rule-set version this answer was resolved against — read at
   * render time from the session's own state, so it always matches whatever
   * `RuleSetPanel` shows in the same session (never a frozen `rs-v2`). */
  versionId: string;
  heading: string;
  checks: readonly PbRecoveryCauseCheck[];
  coveredCount: number;
  proposedCount: number;
  gapCount: number;
  closingLine: string;
  citations: readonly ChatCitation[];
};

function ruleSourcesCause(rule: DiagnosticRule, causeId: string): boolean {
  return Boolean(rule.sourceCauseId?.includes(causeId));
}

/** Computed fresh from `advisory` every call — no module-load snapshot, so
 * an in-session approval that moves the rule-set version is reflected the
 * next time this renders, the same way `RuleSetPanel` is. */
export function pbRecoveryRetrieval(advisory: AdvisorySessionState): PbRecoveryRetrieval {
  const { version, rules, proposed } = liveRuleSet(advisory);
  const causes = MET_DIAGNOSTICS_CAUSES.filter((row) => row.section === PB_RECOVERY_SECTION);

  const checks: PbRecoveryCauseCheck[] = causes.map((cause) => {
    const liveRules = rules.filter((rule) => ruleSourcesCause(rule, cause.id));
    if (liveRules.length > 0) {
      return { causeId: cause.id, cause: cause.cause, state: 'covered', rules: liveRules };
    }
    const proposedRules = proposed
      .map((claim) => claim.value)
      .filter((rule) => ruleSourcesCause(rule, cause.id));
    if (proposedRules.length > 0) {
      return { causeId: cause.id, cause: cause.cause, state: 'proposed', rules: proposedRules };
    }
    return { causeId: cause.id, cause: cause.cause, state: 'gap', rules: [] };
  });

  const coveredCount = checks.filter((row) => row.state === 'covered').length;
  const proposedCount = checks.filter((row) => row.state === 'proposed').length;
  const gapCount = checks.filter((row) => row.state === 'gap').length;

  const citations: ChatCitation[] = checks
    .filter((row) => row.state === 'covered')
    .flatMap((row) => row.rules)
    .filter((rule, index, all) => all.findIndex((r) => r.id === rule.id) === index)
    .map((rule) => ({ label: `${version.id} · ${rule.id}`, id: rule.id }));

  return {
    versionId: version.id,
    heading: `${PB_RECOVERY_SECTION} — ${version.id}, ${coveredCount} of ${checks.length} causes covered:`,
    checks,
    coveredCount,
    proposedCount,
    gapCount,
    closingLine: `${coveredCount} covered · ${proposedCount} proposed (not yet approved) · ${gapCount} gap — no live or proposed rule names them. This is what the live set actually carries, not a completeness claim.`,
    citations,
  };
}

/** Static citation-source registry entries — one per rule that could ever be
 * cited into this section (live, proposed, or neither), independent of
 * session state, keyed by the rule's own stable id (not a version-prefixed
 * id) so the drawer resolves regardless of which rule-set version is
 * current. The version stamp lives only in the chip's display `label`
 * (`pbRecoveryRetrieval`'s `citations`), which is computed at render time. */
export const PB_RECOVERY_CITATION_SOURCES: CitationSource[] = (() => {
  const causeIds = new Set(
    MET_DIAGNOSTICS_CAUSES.filter((row) => row.section === PB_RECOVERY_SECTION).map(
      (row) => row.id,
    ),
  );
  return allRules()
    .filter((rule) => rule.sourceCauseId?.some((id) => causeIds.has(id)))
    .map((rule) => ({
      id: rule.id,
      title: `${rule.id} — ${rule.diagnosis}`,
      documentFilename: 'rule-set.json',
      excerpt: `${rule.signalPattern} → ${rule.diagnosis}. ${rule.recommendedAction}`,
      date: '2026-06-08',
      author: 'Rule set engine — live rule row',
      confidence: 'high',
    }));
})();
